import axios from "axios";

/**
 * Resolve the backend base URL.
 *
 * - In dev (localhost) and Emergent preview, REACT_APP_BACKEND_URL points at
 *   the dev backend domain and we use it as-is.
 * - In production behind a custom domain (e.g. https://speech.flowscribe.com),
 *   the same Emergent ingress proxies /api/* on the same host to the backend.
 *   Using `window.location.origin` keeps cookies first-party (no SameSite=None
 *   cross-site issues) and makes the SAME bundle work on every domain the
 *   app is deployed under.
 *
 * Heuristic: if the build-time env points to an Emergent *preview* host
 * but the page is being served from any other host, prefer the current
 * origin. This avoids the recurring "login works once then drops" symptom
 * caused by Safari/Chrome dropping third-party cookies.
 */
function resolveBackendUrl() {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  if (typeof window === "undefined") return envUrl || "";
  const currentOrigin = window.location.origin;
  if (!envUrl) return currentOrigin;
  try {
    const envHost = new URL(envUrl).host;
    const curHost = window.location.host;
    // Same host → great, use the env URL (lets devs proxy)
    if (envHost === curHost) return envUrl;
    // If the env URL is an Emergent preview/internal domain but the page is
    // served from a custom domain, switch to the current origin so cookies
    // stay first-party.
    if (envHost.endsWith(".emergentagent.com") && !curHost.endsWith(".emergentagent.com")) {
      return currentOrigin;
    }
  } catch {
    /* malformed env URL — fall through */
  }
  return envUrl;
}

const BACKEND_URL = resolveBackendUrl();
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

export function formatErr(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
